import { consola } from 'consola';
import { consoleLevelsFromMinLevel, readLogLevelFromEnv } from './log-level';
import { describeErrorCauseChain } from '../utils/describe-error';
import type { SentryAdapter } from './sentry-adapter.interface';

/**
 * Application Logger with Sentry Integration
 * 
 * Platform-agnostic logger that works on both server (Node.js) and client (Angular/Browser).
 * 
 * Logging Strategy:
 * - FATAL: System-critical failures (database unavailable, service crashes)
 * - ERROR: Failed operations that affect users (auth failures, invalid tokens)
 * - WARN: Degraded states (cache offline, fallback mode)
 * - INFO: Important security/business events (login success, password change, session creation)
 * - DEBUG: Detailed diagnostic info (only in development, never in production)
 * - TRACE: Very detailed diagnostic info (only in development, never in production)
 * 
 * Features:
 * - Automatic Sentry integration
 * - Optional console logging (min level from `LOG_LEVEL` / `VITE_LOG_LEVEL`, default `error`)
 * - Environment-aware (debug/trace only in development)
 * - Structured attributes for filtering and searching
 * - Type-safe context system
 * - Works on both server and client
 */

/**
 * Base log contexts that all modules can use
 * Modules can extend this with their own contexts
 */
export type LogContext =
    | 'session'
    | 'token'
    | 'oauth'
    | 'password'
    | 'validation'
    | 'database'
    | 'cache'
    | 'api'
    | 'http'
    | 'email'
    | 'payment'
    | 'notification'
    | string; // Allow any string for module-specific contexts

/**
 * Logger configuration options
 */
export interface LoggerConfig {
    /** Module name (e.g., 'auth', 'contacts', 'visitors') */
    module: string;
    /** Sentry adapter (platform-specific) */
    sentryAdapter: SentryAdapter;
    /** Whether to also log to console (default: true in development, false in production) */
    logToConsole?: boolean;
    /** Enable/disable specific log levels */
    enabledLevels?: {
        trace?: boolean;
        debug?: boolean;
        info?: boolean;
        warn?: boolean;
        error?: boolean;
        fatal?: boolean;
    };
    /** Enable/disable logging to the local console (Consola) per level */
    consoleLevels?: {
        trace?: boolean;
        debug?: boolean;
        info?: boolean;
        warn?: boolean;
        error?: boolean;
        fatal?: boolean;
    };
    /** Enable/disable logging to Sentry per level */
    sentryLevels?: {
        trace?: boolean;
        debug?: boolean;
        info?: boolean;
        warn?: boolean;
        error?: boolean;
        fatal?: boolean;
    };
}

/**
 * Structured log attributes
 */
export interface LogAttributes {
    userId?: string;
    email?: string;
    sessionId?: string;
    endpoint?: string;
    provider?: string;
    error?: Error | string;
    context?: LogContext;
    operation?: string;
    [key: string]: string | number | boolean | undefined | Error | string[];
}

/**
 * Logger class for structured logging with Sentry integration
 * Platform-agnostic - works on both server and client
 */
export class Logger {
    private readonly module: string;
    private readonly sentryAdapter: SentryAdapter;
    private readonly logToConsole: boolean = false;
    private readonly isDevelopment: boolean = false;
    private readonly enabledLevels: Required<NonNullable<LoggerConfig['enabledLevels']>>;
    private readonly consoleLevels: Required<NonNullable<LoggerConfig['consoleLevels']>>;
    private readonly sentryLevels: Required<NonNullable<LoggerConfig['sentryLevels']>>;
    private readonly consoleLogger: ReturnType<typeof consola.withTag>;

    constructor(config: LoggerConfig) {
        this.module = config.module;
        this.sentryAdapter = config.sentryAdapter;

        // Detect environment (works in both Node.js and browser)
        this.isDevelopment = this.detectDevelopment();

        // Default: log to console in all environments (can be disabled per module)
        this.logToConsole = config.logToConsole ?? true;

        // Default enabled levels
        this.enabledLevels = {
            trace: config.enabledLevels?.trace ?? true,
            debug: config.enabledLevels?.debug ?? true,
            info: config.enabledLevels?.info ?? true,
            warn: config.enabledLevels?.warn ?? true,
            error: config.enabledLevels?.error ?? true,
            fatal: config.enabledLevels?.fatal ?? true,
        };

        this.consoleLogger = consola.withTag(this.module);

        const envConsoleLevels = consoleLevelsFromMinLevel(readLogLevelFromEnv());
        this.consoleLevels = {
            ...envConsoleLevels,
            ...config.consoleLevels,
        };

        const defaultSentryLevels = {
            trace: false,
            debug: false,
            info: false,
            warn: true,
            error: true,
            fatal: true,
        };
        this.sentryLevels = {
            ...defaultSentryLevels,
            ...config.sentryLevels,
        };
    }

    /**
     * Detect if running in development environment
     * Works in both Node.js and browser
     */
    private detectDevelopment(): boolean {
        // Node.js environment
        if (typeof process !== 'undefined' && process.env) {
            return process.env['NODE_ENV'] === 'development';
        }

        // Browser environment (fallback)
        return true; // Default to development if we can't detect
    }

    /**
     * Log trace-level messages (detailed diagnostic information)
     * Only logs in development environment unless explicitly enabled
     */
    trace(message: string, attributes?: LogAttributes): void {
        if (!this.enabledLevels.trace) return;
        if (!this.isDevelopment) return;

        const formatted = this.formatAttributes(attributes);

        if (this.shouldConsoleLog('trace')) {
            this.consoleLogger.trace(message, formatted);
        }

        if (this.shouldSentryLog('trace')) {
            this.sentryAdapter.logger.trace(message, formatted);
        }
    }

    /**
     * Log debug-level messages (debugging information)
     * Only logs in development environment unless explicitly enabled
     */
    debug(message: string, attributes?: LogAttributes): void {
        if (!this.enabledLevels.debug) return;
        if (!this.isDevelopment) return;

        const formatted = this.formatAttributes(attributes);

        if (this.shouldConsoleLog('debug')) {
            this.consoleLogger.debug(message, formatted);
        }

        if (this.shouldSentryLog('debug')) {
            this.sentryAdapter.logger.debug(message, formatted);
        }
    }

    /**
     * Log info-level messages (general informational messages)
     */
    info(message: string, attributes?: LogAttributes): void {
        if (!this.enabledLevels.info) return;

        const formatted = this.formatAttributes(attributes);

        if (this.shouldConsoleLog('info')) {
            this.consoleLogger.info(message, formatted);
        }

        if (this.shouldSentryLog('info')) {
            this.sentryAdapter.logger.info(message, formatted);
        }
    }

    /**
     * Log warning-level messages (warning but not error conditions)
     */
    warn(message: string, attributes?: LogAttributes): void {
        if (!this.enabledLevels.warn) return;

        const formatted = this.formatAttributes(attributes);

        if (this.shouldConsoleLog('warn')) {
            this.consoleLogger.warn(message, formatted);
        }

        if (this.shouldSentryLog('warn')) {
            this.sentryAdapter.logger.warn(message, formatted);
        }
    }

    /**
     * Log error-level messages (error conditions)
     */
    error(message: string, attributes?: LogAttributes): void {
        if (!this.enabledLevels.error) return;

        const formattedAttrs = this.formatAttributes(attributes);

        if (this.shouldConsoleLog('error')) {
            this.consoleLogger.error(message, formattedAttrs);
        }

        if (this.shouldSentryLog('error')) {
            // If an error object is provided, capture it separately for stack traces
            if (attributes?.error) {
                const error = attributes.error instanceof Error
                    ? attributes.error
                    : new Error(typeof attributes.error === 'string' ? attributes.error : 'Unknown error');
                this.sentryAdapter.captureException(error, {
                    tags: {
                        module: this.module,
                        context: attributes.context,
                    },
                    extra: formattedAttrs,
                });
            }

            this.sentryAdapter.logger.error(message, formattedAttrs);
        }
    }

    /**
     * Log fatal-level messages (very severe error events)
     */
    fatal(message: string, attributes?: LogAttributes): void {
        if (!this.enabledLevels.fatal) return;

        const formattedAttrs = this.formatAttributes(attributes);

        if (this.shouldConsoleLog('fatal')) {
            this.consoleLogger.fatal(message, formattedAttrs);
        }

        if (this.shouldSentryLog('fatal')) {
            // Always capture fatal errors
            const error = attributes?.error instanceof Error
                ? attributes.error
                : new Error(message);
            this.sentryAdapter.captureException(error, {
                level: 'fatal',
                tags: {
                    module: this.module,
                    context: attributes?.context,
                },
                extra: formattedAttrs,
            });

            this.sentryAdapter.logger.fatal(message, formattedAttrs);
        }
    }

    private shouldConsoleLog(level: keyof Logger['enabledLevels']): boolean {
        return this.logToConsole && this.consoleLevels[level];
    }

    private shouldSentryLog(level: keyof Logger['enabledLevels']): boolean {
        return this.sentryLevels[level];
    }

    /**
     * Format attributes for Sentry logging
     */
    private formatAttributes(attributes?: LogAttributes): Record<string, unknown> {
        if (!attributes) {
            return { module: this.module };
        }

        const formatted: Record<string, unknown> = {
            module: this.module,
        };

        // Add context
        if (attributes.context) {
            formatted[`${this.module}.context`] = attributes.context;
        }

        // Add user information (sanitized)
        if (attributes.userId) {
            formatted['user.id'] = attributes.userId;
        }
        if (attributes.email) {
            // Only log email domain for privacy
            formatted['user.email_domain'] = attributes.email.split('@')[1] || 'unknown';
        }

        // Add session information
        if (attributes.sessionId) {
            formatted['session.id'] = attributes.sessionId;
        }

        // Add API information
        if (attributes.endpoint) {
            formatted['http.endpoint'] = attributes.endpoint;
        }

        // Add OAuth provider
        if (attributes.provider) {
            formatted['oauth.provider'] = attributes.provider;
        }

        // Add error information
        if (attributes.error) {
            if (attributes.error instanceof Error) {
                formatted['error.message'] = attributes.error.message;
                formatted['error.name'] = attributes.error.name;
                if (attributes.error.stack) {
                    formatted['error.stack'] = attributes.error.stack;
                }
                // Wrappers such as Drizzle's DrizzleQueryError put the driver error in `cause`;
                // without it the log only says which query failed, never why.
                const cause = describeErrorCauseChain(attributes.error);
                if (cause) {
                    formatted['error.cause'] = cause;
                }
            } else {
                formatted['error.message'] = String(attributes.error);
            }
        }

        // Add any other custom attributes
        Object.keys(attributes).forEach(key => {
            if (!['userId', 'email', 'sessionId', 'endpoint', 'provider', 'error', 'context'].includes(key)) {
                const value = attributes[key];
                if (value !== undefined) {
                    formatted[key] = value;
                }
            }
        });

        return formatted;
    }

    /**
     * Sanitize sensitive data from logs
     */
    sanitizeToken(token: string): string {
        if (!token || token.length < 16) {
            return '***';
        }
        return `${token.substring(0, 8)}...${token.substring(token.length - 4)}`;
    }

    /**
     * Sanitize code/token for logging
     */
    sanitizeCode(code: string): string {
        if (!code || code.length < 8) {
            return '***';
        }
        return `${code.substring(0, 8)}...`;
    }
}

/**
 * Create a logger instance for a specific module
 * 
 * @example
 * ```typescript
 * // Server-side
 * import { sentryAdapter } from '@mb/common/server/logger';
 * const logger = createLogger({ 
 *   module: 'auth',
 *   sentryAdapter 
 * });
 * 
 * // Client-side
 * import { sentryAdapter } from '@mb/common/client/logger';
 * const logger = createLogger({ 
 *   module: 'auth',
 *   sentryAdapter 
 * });
 * ```
 */
export function createLogger(config: LoggerConfig): Logger {
    return new Logger(config);
}

/**
 * Helper function to create module-specific log contexts
 * This allows each module to define their own contexts while maintaining type safety
 * 
 * @example
 * ```typescript
 * // In auth module
 * export const AuthContext = defineLogContext({
 *   SESSION: 'session',
 *   TOKEN: 'token',
 *   OAUTH: 'oauth',
 * } as const);
 * 
 * export type AuthLogContext = typeof AuthContext[keyof typeof AuthContext] | LogContext;
 * ```
 */
export function defineLogContext<T extends Record<string, string>>(contexts: T): T {
    return contexts;
}

